import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    ICustomerDetail,
    CustomerCreatePayload,
    CustomerRestoreDeletedPayload,
    CustomerUpdatePayload,
} from "@/customTypings/customer/Customer";
import { authHeader, handleResponse } from "../../utils";
import { postCustomerCreate } from "@/utils/customer/customerCreateConflict";

function siteCustomersBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/customers`;
}

export const customersService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

export type CustomerGetByIdOptions = {
    /** When false, audit display fields are omitted (faster for entity-only reads). Default true. */
    includeAudit?: boolean;
};

/** Loads a customer detail. Pass `{ includeAudit: false }` when audit bar fields are not needed. */
function getById(siteId: string, id: string, options?: CustomerGetByIdOptions) {
    const params = new URLSearchParams();
    if (options?.includeAudit === false) {
        params.set("includeAudit", "false");
    }
    const query = params.toString();
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    const url = `${siteCustomersBase(siteId)}/${encodeURIComponent(id)}${query ? `?${query}` : ""}`;
    return fetch(url, requestOptions).then(handleResponse) as Promise<ICustomerDetail>;
}

function create(siteId: string, payload: CustomerCreatePayload) {
    return postCustomerCreate<ICustomerDetail>(siteId, payload);
}

function restoreDeletedWithCreateValues(siteId: string, payload: CustomerRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteCustomersBase(siteId)}/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<ICustomerDetail>;
}

function update(siteId: string, payload: CustomerUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteCustomersBase(siteId)}/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<ICustomerDetail>>;
}

function disable(siteId: string, id: string) {
    return postQuery(siteId, "disable", id);
}

function enable(siteId: string, id: string) {
    return postQuery(siteId, "enable", id);
}

function softDelete(siteId: string, id: string) {
    return postQuery(siteId, "softdelete", id);
}

function restore(siteId: string, id: string) {
    return postQuery(siteId, "restore", id);
}

function postQuery(siteId: string, action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteCustomersBase(siteId)}/${action}?id=${encodeURIComponent(id)}`, requestOptions).then(
        handleResponse
    );
}
